import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CalendarCheck,
  Check,
  ChevronDown,
  ClipboardList,
  Copy,
  ExternalLink,
  Forward,
  Paperclip,
  RefreshCw,
  Search,
  Tag,
  User,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import {
  apiMessage,
  clearConfirmation,
  confirmCustomer,
  getCustomerLive,
  getEmailRecord,
  getTaskLive,
  listCategories,
  listFilingFolders,
} from '../services/serviceInboxApi';
import CustomerSearch, { MarkButton } from './CustomerSearch';
import JsonModal, { JsonButton } from './JsonModal';
import ManualTask, { matchedCustomer } from './ManualTask';
import BookingSection from './Booking';
import { AppointmentLinks, ListAppointmentChip, useAppointments } from './Appointments';
import { catColor, catLabel } from './categories';
import './ClassificationReview.css'; // the --cat-* palette, via .cat-palette

/**
 * The service inbox's plugin for the generic Mailbox (`src/mailbox/`): what the
 * automation recorded about each message (`stored`: category and summary,
 * customer match, ServiceTitan task and booking), the category and "Would book"
 * filters, and the card on the open message. The generic page imports none of
 * this; it calls the hooks in `serviceInboxMailboxPlugin` below.
 */

const fmtFullDate = (iso) => (iso ? new Date(iso).toLocaleString() : '');

// What the service inbox recorded for a message (`stored`), joined by the
// service on the email's Message-ID. Null means it never processed that mail.
//
// `stored.task` and `stored.booking` are the ServiceTitan side: null when no
// writer ever considered the email, or nobody made a booking. `stored.appointment`
// is what an appointment request asked for, which prefills a booking.

// The review page's wording. An error must never read like "not found".
const MATCH_LABELS = {
  matched: 'matched',
  ambiguous: 'more than one',
  no_match: 'not found',
  insufficient_signals: 'nothing to match on',
  not_attempted: 'not looked up',
  error: 'lookup failed',
};

const CategoryBadge = ({ category, confidence, large = false }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-white font-medium text-gray-800 sm:gap-1.5 ${
      large ? 'px-2 py-0.5 text-sm sm:px-2.5' : 'px-2 py-px text-xs'
    }`}
  >
    <span
      className={`${large ? 'h-2.5 w-2.5' : 'h-2 w-2'} rounded-full`}
      style={{ background: catColor(category) }}
    />
    {catLabel(category)}
    {confidence != null && <span className="font-normal text-gray-500">{confidence}%</span>}
  </span>
);

// A row draws a customer found, more than one found (with how many), or a
// failed lookup. The other "no customer" states leave it empty.
// `iconOnly` (the reading pane's card, where the name is a click away) draws a
// matched customer as the icon alone, with the name on hover. `linked` (the
// open message's card) draws the name larger and opens the customer's page in
// ServiceTitan, when the service could build the link (2026-10-15).
const CustomerBadge = ({ customer, confirmed, iconOnly = false, linked = false }) => {
  // A customer a person marked is the match, whatever the lookup said.
  if (confirmed) customer = { ...confirmed, marked: true };
  if (!customer) return null;
  if (customer.name && iconOnly) {
    return (
      <span
        className="inline-flex shrink-0 items-center rounded-full bg-green-50 p-1 text-green-800 ring-1 ring-inset ring-green-200"
        title={`${customer.name}${customer.office ? ` · ${customer.office}` : ''}${
          customer.marked ? ` (marked by ${customer.confirmed_by})` : ''
        }`}
        aria-label={`Matched customer: ${customer.name}`}
      >
        <UserCheck className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (customer.name) {
    const body = (
      <>
        <UserCheck className={`${linked ? 'h-4 w-4' : 'h-3 w-3'} shrink-0`} />
        <span className="truncate" title={customer.marked ? `Marked by ${customer.confirmed_by}` : undefined}>
          {customer.name}
          {customer.office ? ` · ${customer.office}` : ''}
        </span>
      </>
    );
    const cls = `inline-flex min-w-0 items-center gap-1 rounded-full bg-green-50 text-green-800 ring-1 ring-inset ring-green-200 ${
      linked ? 'px-2.5 py-0.5 text-sm font-medium' : 'px-2 py-px text-xs'
    }`;
    if (linked && customer.servicetitan_url) {
      return (
        <a
          href={customer.servicetitan_url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open the customer in ServiceTitan"
          className={`${cls} hover:bg-green-100`}
        >
          {body}
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </a>
      );
    }
    return <span className={cls}>{body}</span>;
  }
  if (customer.status === 'ambiguous') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-px text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
        <Users className="h-3 w-3" />
        {customer.count > 1 ? `${customer.count} customers` : 'more than one customer'}
      </span>
    );
  }
  if (customer.status === 'error') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-px text-xs text-red-700 ring-1 ring-inset ring-red-200">
        <AlertTriangle className="h-3 w-3" />
        lookup failed
      </span>
    );
  }
  return null;
};

// The task writer's outcomes (TaskStatus on the service side). An error says
// nothing about whether a task exists, so it must not read like "skipped".
const TASK_LABELS = {
  created: 'created',
  skipped: 'skipped',
  error: 'write failed',
  not_attempted: 'not attempted',
  pending: 'in progress',
};

// The automation's forward of the email (2026-10-19): whether it went and to
// whom. A list row says "Forwarded" and names the people on hover; the open
// message's card (`linked`) names them on the mark itself. A test redirect is
// said as such, since the addresses are then the test address, not the rule's.
const FORWARD_MARKS = {
  sent: { label: 'Forwarded', tone: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
  failed: { label: 'Forward failed', tone: 'bg-red-50 text-red-700 ring-red-200' },
  unknown: { label: 'Forward unsure', tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
  claimed: { label: 'Forwarding', tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
};
const ForwardBadge = ({ forward, linked }) => {
  const mark = forward && FORWARD_MARKS[forward.status];
  if (!mark) return null;
  const who = (forward.recipients || []).join(', ');
  const when = forward.at ? ` on ${fmtFullDate(forward.at)}` : '';
  const detail =
    forward.status === 'sent'
      ? `Forwarded by the routing rule to ${who}${when}${forward.redirected ? ' (test redirect)' : ''}`
      : `${mark.label}: ${forward.reason || 'no detail'}${when}`;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-px text-xs ring-1 ring-inset ${mark.tone}`}
      title={detail}
    >
      <Forward className="h-3 w-3" />
      {linked && forward.status === 'sent' ? `Forwarded to ${who}` : mark.label}
      {linked && forward.redirected && forward.status === 'sent' && ' (test redirect)'}
    </span>
  );
};

// A row's ServiceTitan marks: a task written, a failed write, a booking, and
// the automation's forward. The open message's card passes the task's live
// status, which the list does not have, and `linked`, which makes the task's
// mark open it in ServiceTitan (a list row is itself a click target, so its
// marks stay plain).
const ServiceTitanBadges = ({ stored, taskStatus, linked = false }) => (
  <>
    {stored?.task?.status === 'error' && (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-px text-xs text-red-700 ring-1 ring-inset ring-red-200">
        <ClipboardList className="h-3 w-3" />
        Task failed
      </span>
    )}
    {stored?.task?.status === 'created' &&
      (linked && stored.task.servicetitan_url ? (
        <a
          href={stored.task.servicetitan_url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open the task in ServiceTitan"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-px text-xs text-blue-800 ring-1 ring-inset ring-blue-200 hover:bg-blue-100"
        >
          <ClipboardList className="h-3 w-3" />
          {taskStatus ? `Task · ${stTaskStatus(taskStatus).label}` : 'Task'}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-px text-xs text-blue-800 ring-1 ring-inset ring-blue-200">
          <ClipboardList className="h-3 w-3" />
          {taskStatus ? `Task · ${stTaskStatus(taskStatus).label}` : 'Task'}
        </span>
      ))}
    {stored?.would_book?.eligible && stored?.booking?.status !== 'created' && (
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-dashed border-violet-300 bg-violet-50 px-2 py-px text-xs text-violet-800"
        title="The automatic booking rules would book this (automatic booking is off)"
      >
        <CalendarCheck className="h-3 w-3" />
        Would book
      </span>
    )}
    {stored?.booking?.status === 'created' && (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-2 py-px text-xs text-indigo-800 ring-1 ring-inset ring-indigo-200">
        <CalendarCheck className="h-3 w-3" />
        Booked
      </span>
    )}
    <ForwardBadge forward={stored?.forward} linked={linked} />
  </>
);

// `heading` replaces the small uppercase title with a line of its own (the
// matched customer's name stands where "Customer" would); `untitled` drops it.
const Section = ({ icon: Icon, title, heading, untitled = false, children }) => {
  let top = heading;
  if (untitled) top = null;
  else if (!top) {
    top = (
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
    );
  }
  // The untitled section is the card's one-line top (the category and the
  // summary): kept thin, so the card does not push the message down.
  return (
    <div
      className={`border-t border-gray-200 px-3 first:border-t-0 sm:px-4 ${
        untitled ? 'py-1' : 'py-3'
      }`}
    >
      {top}
      {children}
    </div>
  );
};

// The tabs of the two JSON views. ServiceTitan's own objects, read live; and
// everything the service inbox stored for the email (the AI side).
const ST_SECTIONS = [
  { label: 'Customer', pick: (d) => d.customer },
  { label: 'Contacts', pick: (d) => d.customer_contacts?.data ?? d.customer_contacts },
  { label: 'Locations', pick: (d) => d.locations?.data ?? d.locations },
  { label: 'Location contacts', pick: (d) => d.location_contacts },
  { label: 'Errors', pick: (d) => (Object.keys(d.errors || {}).length ? d.errors : null) },
];

const RECORD_SECTIONS = [
  { label: 'Classification', pick: (d) => d.classification },
  { label: 'Model calls', pick: (d) => d.trace?.attempts },
  { label: 'System prompt', pick: (d) => d.trace?.system_prompt, text: true },
  { label: 'User prompt', pick: (d) => d.trace?.user_prompt, text: true },
  { label: 'Tool schema', pick: (d) => d.trace?.tool_schema },
  {
    label: 'Prompt version',
    pick: (d) =>
      d.trace && {
        model: d.trace.model,
        prompt_name: d.trace.prompt_name,
        prompt_version: d.trace.prompt_version,
        prompt_is_fallback: d.trace.prompt_is_fallback,
        duration_ms: d.trace.duration_ms,
      },
  },
  { label: 'Customer lookup', pick: (d) => d.customer_match },
  { label: 'Marked customer', pick: (d) => d.customer_confirmation },
  { label: 'Task', pick: (d) => d.service_titan_task },
  { label: 'Mail actions', pick: (d) => d.actions },
  {
    label: 'Processing',
    pick: (d) => ({
      processing_status: d.processing_status,
      processed_at: d.processed_at,
      attempt_count: d.attempt_count,
      next_retry_at: d.next_retry_at,
      last_error_type: d.last_error_type,
      last_error_message: d.last_error_message,
    }),
  },
  { label: 'Email', pick: (d) => d.email },
];

// What a `{ }` on a customer opens: that customer, read live from ServiceTitan.
const customerView = (c) => ({
  title: `${c.name || 'Customer'} · ST # ${c.customer_id}`,
  subtitle: `Live from ServiceTitan${c.office || c.tenant_location ? ` · ${c.office || c.tenant_location}` : ''}`,
  load: () => getCustomerLive(c.tenant_id, c.customer_id),
  sections: ST_SECTIONS,
});

// A settled lookup's customer, as the Customer section's heading.
const MatchedHeading = ({ customer: c, onInspect }) => (
  <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
      <UserCheck className="h-4 w-4 text-green-700" />
      {c.name}
    </span>
    {c.customer_id != null && (
      <span className="text-sm text-gray-500">
        ST # <StId id={c.customer_id} />
      </span>
    )}
    {onInspect && c.tenant_id && c.customer_id != null && (
      <JsonButton
        // -mr-2 matches the category row's -mx-2, so the two { } line up.
        className="-mr-2 ml-auto"
        label="View the ServiceTitan record"
        onClick={() => onInspect(c)}
      />
    )}
  </div>
);

const Facts = ({ rows }) => (
  <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-0.5 text-sm">
    {rows
      .filter(([, v]) => v != null && v !== '' && v !== false)
      .map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-gray-500">{k}</dt>
          <dd className="break-words text-gray-900">{v}</dd>
        </React.Fragment>
      ))}
  </dl>
);

// A ServiceTitan id with a copy button: the id is what someone types into
// ServiceTitan to find the record, and a name can match more than one.
const StId = ({ id }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no clipboard access (insecure origin): the id is still on screen */
    }
  };
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      {id}
      <button
        type="button"
        onClick={copy}
        className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
        aria-label={`Copy ${id}`}
        title="Copy"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-700" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
};

// What the classifier pulled out of the email to look the customer up by.
const SIGNAL_ROWS = [
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['address', 'Address'],
  ['zip', 'ZIP'],
  ['location_hint', 'Area'],
];

// Only what was extracted. Nothing extracted, or an API that does not send the
// field, draws nothing at all.
const SignalsBlock = ({ signals }) => {
  const rows = SIGNAL_ROWS.filter(([k]) => signals?.[k]);
  if (!rows.length) return null;
  return (
    <div className="mt-3">
      <div className="mb-1 text-xs font-medium text-gray-500">Extracted from the email</div>
      <Facts rows={rows.map(([k, label]) => [label, signals[k]])} />
    </div>
  );
};

const FindCustomer = ({ onMark, onInspect }) => (
  <div className="mt-3">
    <div className="mb-1 text-xs font-medium text-gray-500">ServiceTitan Customer</div>
    <CustomerSearch onMark={onMark} onInspect={onInspect} />
  </div>
);

// A customer a person marked as the match. The lookup's own answer stays
// underneath, one line, so the two are never confused.
const MarkedCustomer = ({ confirmed: m, lookup, onClear, busy }) => (
  <>
    <Facts
      rows={[
        ['Office', m.office],
        ['Location', m.location],
      ]}
    />
    <div className="mt-2 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
      <span>
        Marked as the match by {m.confirmed_by}
        {m.confirmed_at ? `, ${fmtFullDate(m.confirmed_at)}` : ''}
      </span>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          className="text-blue-700 hover:underline disabled:opacity-50"
        >
          Clear match
        </button>
      )}
    </div>
    <div className="mt-0.5 text-xs text-gray-500">
      Lookup:{' '}
      {lookup?.name
        ? `${lookup.name} (ST # ${lookup.customer_id})`
        : lookup?.status === 'ambiguous'
        ? `more than one customer${lookup.count ? ` (${lookup.count})` : ''}`
        : MATCH_LABELS[lookup?.status || 'not_attempted'] || lookup?.status}
    </div>
  </>
);

// Already matched (by the lookup or by a person): "Rematch" opens the same
// signals and search an unmatched email gets, to mark someone else. A mark
// overrides the lookup; Clear match on a marked one goes back to the lookup.
const RematchLink = ({ open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="mt-2 text-xs text-blue-700 hover:underline"
  >
    {open ? 'Cancel rematch' : 'Rematch'}
  </button>
);

// `changing`: open on the search to pick someone else (the Change customer
// quick action), rather than on the match with a link to it.
const CustomerSection = ({
  customer: c,
  confirmed,
  signals,
  onMark,
  onClear,
  onInspect,
  busy,
  changing = false,
}) => {
  const [rematching, setRematching] = useState(changing);
  // A new mark (or a cleared one) is the rematch finished. Not on mount, which
  // would undo `changing`.
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) setRematching(false);
    mounted.current = true;
  }, [confirmed?.tenant_id, confirmed?.customer_id]);

  const matched = Boolean(confirmed || c?.name);
  const search = (
    <>
      <SignalsBlock signals={signals} />
      <FindCustomer onMark={onMark} onInspect={onInspect} />
    </>
  );
  return (
    <>
      {confirmed ? (
        <MarkedCustomer confirmed={confirmed} lookup={c} onClear={onClear} busy={busy} />
      ) : (
        <LookupResult customer={c} onMark={onMark} onInspect={onInspect} busy={busy} />
      )}
      {matched && (
        <RematchLink open={rematching} onToggle={() => setRematching((r) => !r)} />
      )}
      {/* Unmatched: always. Matched: only while rematching. */}
      {(!matched || rematching) && search}
    </>
  );
};

// How each candidate lines up with the email, the review page's marks
// (ClassificationReview.js): the key that found the crowd is the same for every
// card, so on its own it tells none of them apart. A signal the email never
// carried draws nothing; "?" is a phone or email nobody compared, never a miss.
const AGREE_FIELDS = [
  ['name', 'name'],
  ['email', 'email'],
  ['phone', 'phone'],
  ['address', 'address'],
  ['zip', 'ZIP'],
];
const AGREE_MARK = { exact: '✓', close: '≈', differs: '✗', unknown: '?' };
const AGREE_TONE = {
  exact: 'bg-green-50 text-green-800 ring-green-200',
  close: 'bg-green-50 text-green-800 ring-green-200',
  differs: 'bg-gray-50 text-gray-500 ring-gray-200',
  unknown: 'bg-white text-gray-400 ring-gray-200',
};
const AGREE_TITLE = {
  exact: 'matches the email',
  close: 'the same, written differently',
  differs: 'does not match the email',
  unknown: 'not compared',
};
const VIA_LABEL = {
  location: 'a service location',
  customer: 'the customer record',
  both: 'the customer record and a service location',
};
const KEY_LABEL = { email: 'email', phone: 'phone', name_zip: 'name + ZIP', name_address: 'name + address' };
const foundBy = (matchedOn) =>
  (matchedOn || '')
    .split('+')
    .filter(Boolean)
    .map((k) => KEY_LABEL[k] || k)
    .join(', then ');

const HowMatched = ({ c, matchedOn }) => {
  const chips = AGREE_FIELDS.filter(([k]) => c.agreement?.[k]);
  const by = foundBy(matchedOn);
  if (!chips.length && !by) return null;
  return (
    <div className="mt-1">
      {by && (
        <div className="text-xs text-gray-500">
          Found by {by}
          {c.matched_via && ` · via ${VIA_LABEL[c.matched_via] || c.matched_via}`}
        </div>
      )}
      {chips.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {chips.map(([k, label]) => {
            const v = c.agreement[k];
            return (
              <span
                key={k}
                title={`${label}: ${AGREE_TITLE[v] || v}`}
                className={`rounded px-1.5 text-xs ring-1 ring-inset ${AGREE_TONE[v] || ''}`}
              >
                {AGREE_MARK[v] || v} {label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

// `onMark` puts a "Mark as match" button on each candidate of an unsettled
// lookup: the person picks the right one of them.
const LookupResult = ({ customer: c, onMark, onInspect, busy }) => {
  // The name and ST # are the section's heading (MatchedHeading).
  if (c?.name) {
    return (
      <>
        <Facts
          rows={[
            ['Office', c.office],
            ['Location ID', c.location_id != null && <StId id={c.location_id} />],
            ['Location', c.location],
          ]}
        />
        <HowMatched c={c} matchedOn={c.matched_on} />
      </>
    );
  }
  const status = c ? c.status : 'not_attempted';
  const tone =
    status === 'error'
      ? 'text-red-700'
      : status === 'ambiguous'
      ? 'text-amber-700'
      : 'text-gray-600';
  const candidates = c?.candidates || [];
  return (
    <>
      <div className={`text-sm ${tone}`}>
        {status === 'ambiguous' ? 'More than one customer' : `No customer: ${MATCH_LABELS[status] || status}`}
        {status === 'ambiguous' && c.count ? ` (${c.count})` : ''}
      </div>
      {candidates.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-200 rounded border border-gray-200 bg-white">
          {candidates.map((x, i) => (
            <li key={`${x.customer_id}-${i}`} className="flex items-start gap-2 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2">
                  <span className="font-semibold text-gray-900">{x.name || '(no name)'}</span>
                  <span className="text-gray-500">
                    ST # <StId id={x.customer_id} />
                  </span>
                  {onInspect && (
                    <JsonButton label="View the ServiceTitan record" onClick={() => onInspect(x)} />
                  )}
                  {x.is_rnc && (
                    <span className="rounded bg-amber-50 px-1.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                      RNC · out of scope
                    </span>
                  )}
                </div>
                <div className="text-gray-600">
                  {[x.office, x.customer_type].filter(Boolean).join(' · ')}
                </div>
                {(x.addresses || []).map((a) => (
                  <div key={a} className="text-gray-600">
                    {a}
                  </div>
                ))}
                <HowMatched c={x} matchedOn={c.matched_on} />
              </div>
              {onMark && !x.is_rnc && <MarkButton busy={busy} onClick={() => onMark(x)} />}
            </li>
          ))}
        </ul>
      )}
      {/* The platform caps its list at 25; the count is still the true size. */}
      {candidates.length > 0 && c.count > candidates.length && (
        <div className="mt-1 text-xs text-gray-500">
          Showing {candidates.length} of {c.count}.
        </div>
      )}
    </>
  );
};

const Pill = ({ className, children }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-px text-xs font-medium ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

// ServiceTitan's task statuses (TaskStatus, Task Management v2).
const ST_TASK_STATUS = {
  ToDo: { label: 'To do', className: 'bg-gray-100 text-gray-800 ring-gray-300' },
  InProgress: { label: 'In progress', className: 'bg-blue-50 text-blue-800 ring-blue-200' },
  OnHold: { label: 'On hold', className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  Completed: { label: 'Completed', className: 'bg-green-50 text-green-800 ring-green-200' },
  Canceled: { label: 'Canceled', className: 'bg-gray-100 text-gray-500 ring-gray-200' },
};
const stTaskStatus = (s) =>
  ST_TASK_STATUS[s] || { label: s || 'Unknown', className: 'bg-gray-100 text-gray-700 ring-gray-200' };

// Only the priorities worth a second look get colour; Low and Normal stay quiet.
const PRIORITY_CLASS = {
  Urgent: 'bg-red-50 text-red-700 ring-red-200',
  High: 'bg-amber-50 text-amber-800 ring-amber-200',
};

// The service inbox's own outcomes, for a task that never reached ServiceTitan
// (or has not yet): there is nothing live to read for these.
const LEDGER_STATUS_CLASS = {
  error: 'bg-red-50 text-red-700 ring-red-200',
  pending: 'bg-amber-50 text-amber-800 ring-amber-200',
};

// ServiceTitan writes seven fractional digits; trim to milliseconds so every
// browser parses it.
const stDate = (iso) =>
  iso
    ? new Date(iso.replace(/(\.\d{3})\d+/, '$1')).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';

// Where a created task lives in ServiceTitan. A hand-made task records its
// tenant; one the poller made was written against the lookup's customer.
const taskRef = (stored) => {
  const t = stored?.task;
  if (t?.status !== 'created' || !t.task_id) return null;
  const tenantId = t.tenant_id || stored.customer?.tenant_id;
  return tenantId ? { tenantId, taskId: String(t.task_id) } : null;
};

// The task as ServiceTitan holds it now: read each time the message opens, and
// on Refresh. Never cached, since its current state is the point. A refresh
// keeps the last answer on screen until the new one lands.
const useLiveTask = (ref) => {
  const tenantId = ref?.tenantId;
  const taskId = ref?.taskId;
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!taskId) return undefined;
    let live = true;
    setState((s) => ({ ...s, error: null, loading: true }));
    getTaskLive(tenantId, taskId)
      .then((data) => live && setState({ data, error: null, loading: false }))
      .catch(
        (err) =>
          live &&
          setState((s) => ({
            ...s,
            error: apiMessage(err, 'Could not read the task from ServiceTitan.'),
            loading: false,
          }))
      );
    return () => {
      live = false;
    };
  }, [tenantId, taskId, nonce]);
  // An answer for another task (the ref changed under it) is no answer.
  const data = state.data && String(state.data.task_id) === taskId ? state.data : null;
  return { ...state, data, reload: () => setNonce((n) => n + 1) };
};

// What a `{ }` on a task opens: the task, read live again.
const taskView = (ref) => ({
  title: `ServiceTitan task # ${ref.taskId}`,
  subtitle: 'Live from ServiceTitan',
  load: () => getTaskLive(ref.tenantId, ref.taskId),
  sections: [
    { label: 'Task', pick: (d) => d.task },
    { label: 'Comments', pick: (d) => (d.task?.comments?.length ? d.task.comments : null) },
    { label: 'Subtasks', pick: (d) => d.task?.subTasksData?.subTasks?.length ? d.task.subTasksData : null },
    { label: 'Attachments', pick: (d) => (d.task?.attachments?.length ? d.task.attachments : null) },
    { label: 'Names', pick: (d) => ({ employees: d.employees, labels: d.labels }) },
    { label: 'Errors', pick: (d) => (Object.keys(d.errors || {}).length ? d.errors : null) },
  ],
});

// ServiceTitan descriptions are plain text, so a link in one is a bare URL.
// Drawn as its host, since an Outlook link runs to hundreds of characters; the
// whole URL is on hover.
const URL_RE = /(https?:\/\/[^\s<>"]+)/;

const Linkified = ({ text }) =>
  text.split(URL_RE).map((part, i) => {
    if (i % 2 === 0) return part;
    let host = part;
    try {
      host = new URL(part).host;
    } catch {
      /* not a URL after all: shown as written */
    }
    return (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        title={part}
        className="text-blue-700 hover:underline"
      >
        {host}/…
      </a>
    );
  });

// A long description folds to four lines.
const Description = ({ text }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > 280 || text.split('\n').length > 4;
  return (
    <div className="mt-2 rounded-md bg-white px-3 py-2 text-sm text-gray-800 ring-1 ring-inset ring-gray-200">
      <div className={`whitespace-pre-wrap break-words ${long && !open ? 'line-clamp-4' : ''}`}>
        <Linkified text={text} />
      </div>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-1 text-xs text-blue-700 hover:underline"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

// The part of a task ServiceTitan holds, as it is now.
const LiveTask = ({ data }) => {
  const t = data.task;
  const labels = data.labels || {};
  const person = (id) => (id == null ? null : data.employees?.[String(id)] || `Employee ${id}`);
  const open = t.status !== 'Completed' && t.status !== 'Canceled';
  const overdue = open && t.completeBy && new Date(t.completeBy) < new Date();
  const comments = t.comments || [];
  const subtasks = t.subTasksData?.subTasks || [];
  const files = (t.attachments || []).flatMap((g) => g.files || []);
  return (
    <>
      {t.name && <div className="text-sm font-medium text-gray-900 break-words">{t.name}</div>}
      {t.description && <Description text={t.description} />}
      <div className="mt-2">
        <Facts
          rows={[
            ['Assigned to', person(t.assignedToId)],
            ['Reported by', person(t.reportedById)],
            [
              'Due',
              t.completeBy && (
                <span className={overdue ? 'font-medium text-red-700' : undefined}>
                  {stDate(t.completeBy)}
                  {overdue && ' · overdue'}
                </span>
              ),
            ],
            ['Type', labels.type],
            ['Source', labels.source],
            ['Customer', t.customerName && `${t.customerName}${t.customerId ? ` · ST # ${t.customerId}` : ''}`],
            ['Job', t.jobNumber && `# ${t.jobNumber}`],
            ['Resolution', !open && labels.resolution],
            ['Closed', t.closedOn && stDate(t.closedOn)],
            ['Created', stDate(t.createdOn)],
            ['Updated', t.modifiedOn && stDate(t.modifiedOn)],
          ]}
        />
      </div>
      {comments.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-medium text-gray-500">Comments ({comments.length})</div>
          <ol className="space-y-1.5">
            {comments.map((c, i) => (
              <li key={i} className="rounded-md bg-white px-3 py-1.5 ring-1 ring-inset ring-gray-200">
                <div className="text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{person(c.employeeId) || 'Someone'}</span>
                  {c.createdOn && ` · ${stDate(c.createdOn)}`}
                </div>
                <div className="whitespace-pre-wrap break-words text-sm text-gray-900">{c.comment}</div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {subtasks.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-medium text-gray-500">Subtasks ({subtasks.length})</div>
          <ul className="space-y-0.5 text-sm">
            {subtasks.map((s, i) => (
              <li key={s.subtaskNumber ?? i} className="flex items-start gap-1.5">
                {s.isClosed ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
                ) : (
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-sm ring-1 ring-inset ring-gray-400" />
                )}
                <span className="break-words">
                  <span className={s.isClosed ? 'text-gray-500 line-through' : 'text-gray-900'}>{s.name}</span>
                  {s.assignedToId != null && (
                    <span className="text-gray-500"> · {person(s.assignedToId)}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {files.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <Paperclip className="h-3.5 w-3.5 text-gray-500" />
          {files.map((f, i) =>
            /^https:\/\//.test(f.url || '') ? (
              <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                {f.filename || 'file'}
              </a>
            ) : (
              <span key={i} className="text-gray-700">{f.filename || 'file'}</span>
            )
          )}
        </div>
      )}
    </>
  );
};

// The section's heading: which task, and where it stands. Live status and
// priority when ServiceTitan answered; the service inbox's own outcome for a
// task that never reached it.
const TaskHeading = ({ task: t, live, onRefresh, onInspect }) => {
  const st = live.data?.task;
  const status = st && stTaskStatus(st.status);
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <ClipboardList className="h-4 w-4 text-gray-500" />
        {t.task_id ? (
          <>
            Task # <StId id={t.task_id} />
          </>
        ) : (
          'ServiceTitan task'
        )}
      </span>
      {status && <Pill className={status.className}>{status.label}</Pill>}
      {/* Its page in ServiceTitan, where it is edited; built by the service inbox
          from the office's web account. */}
      {t.servicetitan_url && (
        <a
          href={t.servicetitan_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
        >
          Open in ServiceTitan
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {st?.priority && (
        <Pill className={PRIORITY_CLASS[st.priority] || 'bg-white text-gray-600 ring-gray-200'}>
          {st.priority}
        </Pill>
      )}
      {!t.task_id && (
        <Pill className={LEDGER_STATUS_CLASS[t.status] || 'bg-gray-100 text-gray-700 ring-gray-200'}>
          {TASK_LABELS[t.status] || t.status}
        </Pill>
      )}
      {onRefresh && (
        <span className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onRefresh}
            disabled={live.loading}
            className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
            title="Read the task from ServiceTitan again"
            aria-label="Refresh the task"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${live.loading ? 'animate-spin' : ''}`} />
          </button>
          <JsonButton label="View the ServiceTitan task" onClick={onInspect} />
        </span>
      )}
    </div>
  );
};

// How the task came to be, from the service inbox's own record. A task that
// exists says only who made it, a person or the poller: when is on the live
// task above, and the category on the card. One that was not made says why,
// with when and for which category, since there is nothing live to show.
const TaskLedger = ({ task: t }) => {
  const by = t.created_by ? `by ${t.created_by}` : 'automatically';
  const made = { created: `Created ${by}`, pending: `Being created ${by}` }[t.status];
  if (made) return <div className="text-xs text-gray-500">{made}</div>;
  const parts = [
    t.attempted_at && stDate(t.attempted_at),
    t.category && catLabel(t.category),
  ].filter(Boolean);
  return (
    <>
      {t.reason && <div className="text-sm text-gray-700 break-words">{t.reason}</div>}
      {parts.length > 0 && <div className="mt-1 text-xs text-gray-500">{parts.join(' · ')}</div>}
    </>
  );
};

const TaskSection = ({ task: t, live, taskKnown }) => {
  if (!t) return <div className="text-sm text-gray-500">No task considered.</div>;
  return (
    <>
      {live.data && <LiveTask data={live.data} />}
      {!live.data && live.loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          Reading the task from ServiceTitan…
        </div>
      )}
      {live.error && <div className="text-sm text-red-700">{live.error}</div>}
      {t.status === 'created' && !taskKnown && (
        <div className="text-sm text-gray-500">No ServiceTitan office recorded, so the task cannot be read.</div>
      )}
      <div className={live.data || live.loading || live.error ? 'mt-3 border-t border-gray-200 pt-2' : ''}>
        <TaskLedger task={t} />
      </div>
    </>
  );
};

// The reading pane's record of what the service inbox did with the message.
// Coloured down the left by category, so it reads at a glance.
// A task can be made by hand unless one exists or is in flight; an error may
// still be retried, since it says nothing certain either way.
const canCreateTask = (task) => !task || !['created', 'pending'].includes(task.status);

// A line under the card, there whether or not the details are open: who the
// customer is, and what ServiceTitan already holds for them, each a way in.
// The task's mark opens the task and each appointment's opens its job. It is
// outside the card and its coloured edge, and so outside the row that toggles
// the details (a link inside a button is not one).
// At its right end, the quick actions (2026-10-15, after watching someone
// hunt for them inside the details): Create task, Book and Find customer,
// each opening that one thing in a panel beneath, with the details left shut.
const QUICK_BTN =
  'inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50';

const CardBasics = ({ stored, taskStatus, appointments, quick, onQuick }) => {
  const matched = Boolean(matchedCustomer(stored));
  const taskable = canCreateTask(stored.task);
  const bookable = !['created', 'pending'].includes(stored.booking?.status);
  const pressed = (k) => (quick === k ? 'border-blue-300 bg-blue-50 text-blue-800' : '');
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-2 px-1">
      <CustomerBadge customer={stored.customer} confirmed={stored.confirmed} linked />
      <ServiceTitanBadges stored={stored} taskStatus={taskStatus} linked />
      <AppointmentLinks appointments={appointments} />
      <span className="ml-auto flex flex-wrap items-center gap-1.5">
        {taskable && (
          <button type="button" onClick={() => onQuick('task')} className={`${QUICK_BTN} ${pressed('task')}`}>
            <ClipboardList className="h-4 w-4" />
            Create task
          </button>
        )}
        {bookable && (
          <button type="button" onClick={() => onQuick('book')} className={`${QUICK_BTN} ${pressed('book')}`}>
            <CalendarCheck className="h-4 w-4" />
            Book
          </button>
        )}
        <button
          type="button"
          onClick={() => onQuick('customer')}
          className={`${QUICK_BTN} ${pressed('customer')}`}
        >
          <Search className="h-4 w-4" />
          {matched ? 'Change customer' : 'Find customer'}
        </button>
      </span>
    </div>
  );
};

// What a quick action opens, under the basics line. A task and a booking each
// need a customer, so on an unmatched email both start with the search and
// turn into their form once one is marked.
const QUICK_TITLE = {
  task: 'New ServiceTitan task',
  book: 'New ServiceTitan booking',
  customer: 'Customer',
};

const QuickPanel = ({ stored, quick, onClose, customerSection, onTaskCreated, onBooked, onInspect }) => {
  const matched = matchedCustomer(stored);
  return (
    <div className="mt-2 rounded-md border border-blue-200 bg-white">
      <div className="flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-900">
        <span className="flex-1">{QUICK_TITLE[quick]}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-0.5 text-blue-700 hover:bg-blue-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-3 py-3 sm:px-4">
        {quick === 'customer' && customerSection}
        {quick !== 'customer' && !matched && (
          <>
            <div className="mb-2 text-sm text-gray-600">
              {quick === 'task' ? 'A task' : 'An appointment'} is made for a customer: find and
              mark the customer first.
            </div>
            {customerSection}
          </>
        )}
        {quick === 'task' && matched && (
          <ManualTask
            key={`${stored.email_id}|${stored.confirmed?.customer_id ?? ''}`}
            stored={stored}
            onCreated={onTaskCreated}
            autoOpen
          />
        )}
        {quick === 'book' && matched && (
          <BookingSection stored={stored} onBooked={onBooked} onInspect={onInspect} autoOpen />
        )}
      </div>
    </div>
  );
};

// Opens on the classification and the basics line; the customer, task and
// appointment sections sit behind "Show details". Keyed by message in the page, so every
// message opens collapsed.
// `actions`: the page's own controls (its ×), drawn at the end of the first line.
const StoredCard = ({ stored, onTaskCreated, onBooked, onConfirmed, actions = null }) => {
  const [expanded, setExpanded] = useState(false);
  // The quick action open under the card, if any: 'task', 'book' or 'customer'.
  const [quick, setQuick] = useState(null);
  // The JSON view open over the card, if any (JsonModal props).
  const [inspect, setInspect] = useState(null);
  const inspectCustomer = (c) => setInspect(customerView(c));
  const [busy, setBusy] = useState(false);
  const [markError, setMarkError] = useState(null);
  // Read when the message opens, not when the details expand, so the collapsed
  // badge can say where the task stands.
  const ref = taskRef(stored);
  const live = useLiveTask(ref);
  // What the matched customer already has open in ServiceTitan. Read on open
  // too, for the links on the basics line; asked again when the match
  // changes. RNC is out of scope, so it is not asked about.
  const appointments = useAppointments(stored?.email_id, appointmentKey(stored));
  if (!stored) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 py-2 pl-4 pr-2 text-sm text-gray-500">
        <span className="min-w-0 flex-1">The service inbox has no record of this message.</span>
        {actions}
      </div>
    );
  }
  // Mark or clear, then hand the stored mark (or null) up to the page.
  const mark = async (call) => {
    setBusy(true);
    setMarkError(null);
    try {
      onConfirmed(await call());
      // Marked from the Customer quick panel: done. From Create task: the
      // panel now shows the task form.
      if (quick === 'customer') setQuick(null);
    } catch (err) {
      setMarkError(apiMessage(err, 'Could not save the match.'));
    } finally {
      setBusy(false);
    }
  };
  const customerSection = (changing = false) => (
    <>
      <CustomerSection
        key={changing ? 'changing' : 'shown'}
        customer={stored.customer}
        confirmed={stored.confirmed}
        signals={stored.signals}
        busy={busy}
        changing={changing}
        onMark={(c) => mark(() => confirmCustomer(stored.email_id, c))}
        onClear={() => mark(() => clearConfirmation(stored.email_id).then(() => null))}
        onInspect={inspectCustomer}
      />
      {markError && <div className="mt-1 text-sm text-red-700">{markError}</div>}
    </>
  );
  const taskCreated = (task) => {
    onTaskCreated(task);
    if (task?.status === 'created' || task?.status === 'pending') setQuick(null);
  };
  const booked = (booking) => {
    onBooked(booking);
    if (booking?.status === 'created' || booking?.status === 'pending') setQuick(null);
  };
  return (
    <>
      <div
        className="overflow-hidden rounded-md border border-gray-200 border-l-4 bg-gray-50"
        style={{ borderLeftColor: catColor(stored.category) }}
      >
        {/* The pill says it all: no title, collapsed or expanded. The whole row is
            the toggle; the chevron only shows which way it will go. */}
        <Section icon={Tag} title="Classification" untitled>
          {/* items-start: on a phone the summary wraps under the badges, and the
              { } and the page's × stay at the top right of the first line. */}
          <div className="-mx-2 -my-1 flex items-start gap-1 sm:items-center">
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              title={expanded ? 'Hide details' : 'Show details'}
              className="group flex min-w-0 flex-1 items-start gap-2 rounded py-1 pl-0 pr-1 text-left hover:bg-gray-100 sm:items-center sm:pr-2"
            >
              {/* From sm up, one line: the summary truncates so the chevron and
                  the { } stay on it. On a phone that left the summary a few
                  letters wide, so there the badges take the first line and the
                  summary gets its own, up to two lines, beneath them. */}
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 overflow-hidden sm:flex-nowrap">
                <CategoryBadge category={stored.category} large />
                {/* The AI's one-line summary, which used to be written onto the
                    subject; the full text is on hover. */}
                {stored.summary && (
                  <>
                    <span
                      className="hidden min-w-0 flex-1 truncate text-sm text-gray-700 sm:block"
                      title={stored.summary}
                    >
                      {stored.summary}
                    </span>
                    <span className="order-last line-clamp-2 w-full text-sm text-gray-700 sm:hidden">
                      {stored.summary}
                    </span>
                  </>
                )}
              </span>
              <span className="shrink-0 rounded-full p-1 text-gray-500 ring-1 ring-inset ring-gray-300 group-hover:bg-white group-hover:text-gray-800">
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
                />
              </span>
              <span className="sr-only">{expanded ? 'Hide details' : 'Show details'}</span>
            </button>
            {/* The AI side: everything stored for this email, as JSON. */}
            <JsonButton
              className="shrink-0"
              label="View the AI record"
              onClick={() =>
                setInspect({
                  title: 'Service inbox record',
                  subtitle: 'Classification, prompts, model calls, lookup and task, as stored',
                  load: () => getEmailRecord(stored.email_id),
                  sections: RECORD_SECTIONS,
                })
              }
            />
            {actions}
          </div>
        </Section>
        {expanded && (
          <>
            <Section
              icon={User}
              title="Customer"
              heading={
                (stored.confirmed || stored.customer?.name) && (
                  <MatchedHeading
                customer={stored.confirmed || stored.customer}
                onInspect={inspectCustomer}
              />
                )
              }
            >
              {quick === 'customer' || quick === 'task' ? (
                <div className="text-sm text-gray-500">Open in the panel below.</div>
              ) : (
                customerSection()
              )}
            </Section>
            {stored.task !== undefined && (
              <Section
                icon={ClipboardList}
                title="ServiceTitan Task"
                heading={
                  stored.task && (
                    <TaskHeading
                      task={stored.task}
                      live={live}
                      onRefresh={ref ? live.reload : null}
                      onInspect={() => setInspect(taskView(ref))}
                    />
                  )
                }
              >
                <TaskSection task={stored.task} live={live} taskKnown={!!ref} />
                {canCreateTask(stored.task) && !matchedCustomer(stored) && (
                  <div className="mt-2 text-sm text-gray-500">
                    Mark the customer as the match to create a task.
                  </div>
                )}
                {canCreateTask(stored.task) && matchedCustomer(stored) && (
                  <ManualTask
                    key={`${stored.email_id}|${stored.confirmed?.customer_id ?? ''}`}
                    stored={stored}
                    onCreated={taskCreated}
                  />
                )}
              </Section>
            )}
            <Section icon={CalendarCheck} title="Appointment">
              <BookingSection
                stored={stored}
                onBooked={booked}
                onInspect={setInspect}
                appointments={appointments}
              />
            </Section>
          </>
        )}
        {inspect && <JsonModal {...inspect} onClose={() => setInspect(null)} />}
      </div>
      {/* Under the card, not in it: the category's coloured edge is the
          classification's, and these are what ServiceTitan holds. */}
      <CardBasics
        stored={stored}
        taskStatus={live.data?.task?.status}
        appointments={appointments}
        quick={quick}
        onQuick={(k) => setQuick((q) => (q === k ? null : k))}
      />
      {quick && (
        <QuickPanel
          stored={stored}
          quick={quick}
          onClose={() => setQuick(null)}
          customerSection={customerSection(true)}
          onTaskCreated={taskCreated}
          onBooked={booked}
          onInspect={setInspect}
        />
      )}
    </>
  );
};

const selectClass =
  'min-w-0 flex-1 rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

// The category select and the "Would book" toggle, as rows of the mailbox's
// Filter panel. The one customer a person can filter by is picked from the
// folder breakdown and has no control here; the chip under the search box
// (`filterChips`) is where it shows and is cleared.
const FilterControls = ({ filters, options, onChange }) => (
  <>
    <label className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-xs text-gray-500">Category</span>
      <select
        value={filters.category}
        onChange={(e) => onChange({ category: e.target.value })}
        className={selectClass}
        aria-label="Category"
      >
        <option value="">Any category</option>
        {(options || []).map((c) => (
          <option key={c} value={c}>
            {catLabel(c)}
          </option>
        ))}
        <option value="unprocessed">Not processed</option>
      </select>
    </label>
    {/* For testing the booking rules: nothing books automatically. */}
    <label className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-xs text-gray-500">Booking</span>
      <button
        type="button"
        onClick={() => onChange({ book: !filters.book })}
        aria-pressed={filters.book}
        title="Only emails the automatic booking rules would book (automatic booking is off)"
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm ${
          filters.book
            ? 'border-violet-300 bg-violet-50 text-violet-800'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <CalendarCheck className="h-3.5 w-3.5" />
        Would book
      </button>
    </label>
  </>
);

// Whose appointments an email's card and row ask about: the matched customer,
// or nobody (null) when there is none or it is RNC, which is out of scope.
const appointmentKey = (stored) => {
  const who = stored ? matchedCustomer(stored) : null;
  return who && !who.is_rnc ? `${who.tenant_id}:${who.customer_id}` : null;
};

// A list row's marks: category, customer, ServiceTitan.
const ListBadges = ({ message: m }) =>
  m.stored ? (
    <div className="mt-1 flex min-w-0 items-center gap-1.5">
      <CategoryBadge category={m.stored.category} />
      <CustomerBadge customer={m.stored.customer} confirmed={m.stored.confirmed} />
      <ServiceTitanBadges stored={m.stored} />
      <ListAppointmentChip emailId={m.stored.email_id} customerKey={appointmentKey(m.stored)} />
    </div>
  ) : null;

// A thread row's mark: its category.
const ThreadBadge = ({ message: m }) =>
  m.stored ? <CategoryBadge category={m.stored.category} /> : null;

// The card on the open message. A change it makes to `stored` (a marked
// customer, a task, a booking) goes back through `onUpdate` to the message and
// its list row.
const MessageCard = ({ message, onUpdate, actions = null }) => {
  const setStored = (patch) =>
    onUpdate((m) => (m.stored ? { ...m, stored: { ...m.stored, ...patch } } : m));
  return (
    <StoredCard
      stored={message.stored}
      onTaskCreated={(task) => setStored({ task })}
      onBooked={(booking) => setStored({ booking })}
      onConfirmed={(confirmed) => setStored({ confirmed })}
      actions={actions}
    />
  );
};

// Everything in the folder on screen, broken down by what the service inbox
// knows about it: categories, and whether a customer was found. For the
// Mailbox's empty reading pane; reads the folder's newest few thousand
// messages, so it loads when the folder changes (and on Refresh), not every
// minute. A category filters the list.
// What the lookup concluded, in the order of the bar, with its colour.
const CUSTOMER_ROWS = [
  ['found', 'Customer found', 'bg-emerald-500'],
  ['ambiguous', 'Possibly one of several', 'bg-amber-400'],
  ['no_match', 'No customer found', 'bg-gray-400'],
  ['insufficient_signals', 'Not enough to look up', 'bg-gray-200'],
  ['error', 'Lookup failed', 'bg-red-500'],
  ['not_looked_up', 'Not looked up', 'bg-slate-600'],
];

const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

export const customerCounts = (status = {}) => ({
  found: (status.matched || 0) + (status.confirmed || 0),
  ambiguous: status.ambiguous || 0,
  no_match: status.no_match || 0,
  insufficient_signals: status.insufficient_signals || 0,
  error: status.error || 0,
  not_looked_up: (status.not_looked_up || 0) + (status.not_attempted || 0),
});

// `query`: the list's filters as sent to the service. Of them, the figures
// follow the category only (2026-10-15): the words, the flag, the dates and
// Would book are left out, so the customers shown are the folder's for that
// kind of mail, whatever else the list is narrowed to.
const FolderBreakdown = ({ api, folder, query, onFilter }) => {
  const [state, setState] = useState({ id: null, data: null, error: null, loading: false });
  const [again, setAgain] = useState(0);
  const category = query?.category || '';
  const counted = category ? { category } : {};
  useEffect(() => {
    if (!folder?.id || !api?.folderBreakdown) return undefined;
    let live = true;
    setState((s) => ({ ...s, id: folder.id, loading: true, error: null }));
    api
      .folderBreakdown(folder.id, counted)
      .then((r) => live && setState({ id: folder.id, data: r, error: null, loading: false }))
      .catch((err) =>
        live &&
        setState({
          id: folder.id,
          data: null,
          error: apiMessage(err, "Could not work out this folder's figures."),
          loading: false,
        })
      );
    return () => {
      live = false;
    };
  }, [folder?.id, again, category]); // eslint-disable-line
  if (!folder) return null;
  const r = state.id === folder.id ? state.data : null;
  const d = r?.data;
  const counts = customerCounts(d?.customer_status);
  const label = (k) =>
    k ? d?.labels?.[k] || catLabel(k) : 'Unclassified';
  const max = Math.max(1, ...(d?.categories || []).map((c) => c.count));
  return (
    <section className="space-y-4 text-sm">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <BarChart3 className="h-3.5 w-3.5" />
        <span className="flex-1">
          In this folder{category ? `: ${label(category)}` : ''}
        </span>
        <button
          type="button"
          onClick={() => setAgain((n) => n + 1)}
          disabled={state.loading}
          className="font-normal normal-case text-blue-700 hover:underline disabled:opacity-50"
        >
          {state.loading ? 'Working it out…' : 'Refresh'}
        </button>
      </h3>
      {state.error && <div className="text-gray-500">{state.error}</div>}
      {!d && !state.error && <div className="text-gray-500">Working it out…</div>}
      {d && (
        <>
          {/* Filtered to one category, the graph would be one bar: left out. */}
          {!category && (
          <div>
            <div className="mb-1 text-xs text-gray-500">Categories</div>
            <ul className="space-y-1">
              {d.categories.map((c) => (
                <li key={c.category || 'none'}>
                  <button
                    type="button"
                    onClick={() => c.category && onFilter({ category: c.category })}
                    disabled={!c.category}
                    className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent"
                  >
                    <span className="w-40 shrink-0 truncate text-gray-800">{label(c.category)}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded bg-gray-100">
                      <span
                        className="block h-full"
                        style={{ width: `${(c.count / max) * 100}%`, background: catColor(c.category) }}
                      />
                    </span>
                    <span className="w-10 shrink-0 text-right tabular-nums text-gray-600">{c.count}</span>
                  </button>
                </li>
              ))}
              {d.not_processed > 0 && (
                <li>
                  <button
                    type="button"
                    onClick={() => onFilter({ category: 'unprocessed' })}
                    className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-gray-50"
                  >
                    <span className="w-40 shrink-0 truncate italic text-gray-500">Not processed</span>
                    <span className="h-2 flex-1" />
                    <span className="w-10 shrink-0 text-right tabular-nums text-gray-500">
                      {d.not_processed}
                    </span>
                  </button>
                </li>
              )}
            </ul>
          </div>
          )}
          <div>
            <div className="mb-2 text-xs text-gray-500">Customers</div>
            {(() => {
              const shown = CUSTOMER_ROWS.filter(([k]) => counts[k] > 0);
              const total = shown.reduce((n, [k]) => n + counts[k], 0);
              if (!total) return <div className="text-gray-500">No emails looked up.</div>;
              return (
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <div
                    className="flex h-2.5 overflow-hidden rounded-full bg-gray-100"
                    role="img"
                    aria-label="How the folder's emails matched a customer"
                  >
                    {shown.map(([k, text, colour]) => (
                      <span
                        key={k}
                        className={colour}
                        style={{ width: `${(counts[k] / total) * 100}%` }}
                        title={`${text}: ${counts[k]}`}
                      />
                    ))}
                  </div>
                  <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                    {shown.map(([k, text, colour]) => (
                      <li key={k} className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colour}`} />
                        <span className="min-w-0 flex-1 truncate text-gray-700">{text}</span>
                        <span className="tabular-nums font-medium text-gray-900">
                          {counts[k].toLocaleString()}
                        </span>
                        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-gray-500">
                          {pct(counts[k], total)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            {d.customers?.length > 0 && (
              <ul className="mt-5 divide-y divide-gray-100 rounded-md border border-gray-200">
                {d.customers.map((c) => (
                  <li key={`${c.tenant_id}:${c.customer_id}`}>
                    <button
                      type="button"
                      onClick={() =>
                        onFilter({
                          customer: `${c.tenant_id}:${c.customer_id}`,
                          customerName: c.name || `Customer ${c.customer_id}`,
                        })
                      }
                      title="Show only this customer's emails"
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-blue-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-gray-900">
                          {c.name || `Customer ${c.customer_id}`}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {[c.office, `ST #${c.customer_id}`].filter(Boolean).join(' · ')}
                        </span>
                        {c.latest_summary && (
                          <span
                            className="block truncate text-xs text-gray-600"
                            title={c.latest_summary}
                          >
                            {c.latest_at &&
                              `${new Date(c.latest_at).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                              })}: `}
                            {c.latest_summary}
                          </span>
                        )}
                      </span>
                      {c.tasks > 0 && (
                        <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 text-xs text-emerald-700 ring-1 ring-inset ring-emerald-200">
                          {c.tasks} task{c.tasks === 1 ? '' : 's'}
                        </span>
                      )}
                      {c.bookings > 0 && (
                        <span className="shrink-0 rounded-full bg-violet-50 px-1.5 text-xs text-violet-700 ring-1 ring-inset ring-violet-200">
                          {c.bookings} appointment{c.bookings === 1 ? '' : 's'}
                        </span>
                      )}
                      <span className="w-16 shrink-0 text-right tabular-nums text-gray-600">
                        {c.count} email{c.count === 1 ? '' : 's'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* Said only when the figures do not cover the whole folder. */}
          {!r.complete && (
            <div className="text-xs text-gray-400">
              {`The newest ${r.scanned.toLocaleString()} of ${Number(folder.total || 0).toLocaleString()} messages`}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export const serviceInboxMailboxPlugin = {
  // The --cat-* palette the category colours come from.
  className: 'cat-palette',
  // Where the service inbox files new mail, and staff work from.
  defaultFolder: 'needs review',
  // `customer` is "<tenant id>:<customer id>", set from the folder breakdown;
  // `customerName` only labels its chip.
  filterDefaults: { category: '', book: false, customer: '', customerName: '' },
  filtersFromUrl: (params) => ({
    category: params.get('category') || '',
    book: params.get('book') === '1',
    customer: /^\d+:\d+$/.test(params.get('customer') || '') ? params.get('customer') : '',
    customerName: params.get('cname') || '',
  }),
  filtersToUrl: (f) => ({
    ...(f.category ? { category: f.category } : {}),
    ...(f.book ? { book: '1' } : {}),
    ...(f.customer ? { customer: f.customer, cname: f.customerName || '' } : {}),
  }),
  filterActive: (f) => Boolean(f.category || f.book || f.customer),
  // In words, for the list's "No messages match …".
  filterLabels: (f) => [
    ...(f.category ? [f.category === 'unprocessed' ? 'Not processed' : catLabel(f.category)] : []),
    ...(f.book ? ['Would book'] : []),
    ...(f.customer ? [f.customerName || 'One customer'] : []),
  ],
  // The same, as chips under the search box, each clearing its own filter.
  filterChips: (f) => [
    ...(f.category
      ? [
          {
            label: f.category === 'unprocessed' ? 'Not processed' : catLabel(f.category),
            clear: { category: '' },
            clearLabel: 'Any category',
          },
        ]
      : []),
    ...(f.book ? [{ label: 'Would book', clear: { book: false }, clearLabel: 'Any booking' }] : []),
    ...(f.customer
      ? [
          {
            label: `Customer: ${f.customerName || f.customer}`,
            clear: { customer: '', customerName: '' },
            clearLabel: 'Any customer',
          },
        ]
      : []),
  ],
  filterQuery: (f) => ({
    category: f.category || undefined,
    would_book: f.book ? true : undefined,
    customer: f.customer || undefined,
  }),
  loadFilterOptions: () => listCategories(),
  // The folder tree can show only these (plus Inbox and Drafts), remembered
  // per browser. Archive is a filing folder but left out of the list.
  loadFolderScope: () =>
    listFilingFolders().then((names) => names.filter((n) => n.toLowerCase() !== 'archive')),
  folderScopeLabel: 'Service inbox folders only',
  FilterControls,
  ListBadges,
  ThreadBadge,
  MessageCard,
  // What the empty reading pane adds below the mailbox's own overview.
  Overview: FolderBreakdown,
};

export default serviceInboxMailboxPlugin;
